import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, Button,
  FormControlLabel, Switch, Divider, Autocomplete, TextField, Chip, LinearProgress,
  IconButton,
} from '@mui/material';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import LocalPrintshopRounded from '@mui/icons-material/LocalPrintshopRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import JsBarcode from 'jsbarcode';

import {
  getInkSticker, getArtworkThumbnail, InkStickerData, getJobGroups, JobGroup, jobGroupLabel,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { serial } from './board/useArtworkSeparations';
import { TOKENS } from './board/ganttConfig';
import ArtworkPathDialog from './ArtworkPathDialog';
import { useBarcodeScanner } from '../wms/useBarcodeScanner';
import ManualScanInput from '../wms/ManualScanInput';

/**
 * InkStickerView — the scan-to-print bridge (Phase 1 of the ink → Prism MIS
 * roadmap, docs/production-ink-mis-roadmap.md).
 *
 * A terminal page: scan the Job Bag's existing Code 128 job number (or type it),
 * and a self-adhesive sticker renders showing the per-print-unit ink layout
 * (U1..U7 → colour → sourced series → demand kg), any unassigned ink, the total
 * kg and the artwork thumbnail — then print it and stick it to the Job Bag.
 * Printed on A4 stock (a batch run prints one sticker per sheet) and guillotined
 * down to the sticker's own border — @page size below must match the loaded
 * paper, or the driver centres the declared page inside the physical sheet.
 *
 * Browser-native window.print() only (no server-side PDF — see the 2025-12-07
 * incident note in CLAUDE.md). Read-only; nothing is written to Prism.
 */

const THUMB_WIDTH = 360;
const LMQ_RED = '#d32f2f';

// Fixed sticker footprint, sized for the worst case (all 7 ink units + both
// coatings filled) so every sticker prints at the SAME size regardless of a
// job's actual unit count. That's what makes batch printing guillotine-able:
// stack N printed sheets and make one cut along the border, instead of a
// different cut line per job. Content shorter than this just leaves blank
// space at the bottom (plain block layout — nothing here re-centers it).
const STICKER_WIDTH_MM = 160;
const STICKER_HEIGHT_MM = 205;
// On-screen preview keeps the existing 460px width but adopts the print
// aspect ratio, so what you see on screen is proportional to what prints.
const SCREEN_WIDTH_PX = 460;
const SCREEN_HEIGHT_PX = SCREEN_WIDTH_PX * (STICKER_HEIGHT_MM / STICKER_WIDTH_MM);

// This printer's own registration fudge, measured off a real test print —
// separate from @page's margin below (that's the intended design offset,
// 2-3mm). A different printer/tray would get its own calibration here.
const PRINTER_CALIBRATION_X_MM = 5;
const PRINTER_CALIBRATION_Y_MM = 1;

const kg = (v: number | null | undefined) =>
  (v == null ? '—' : `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`);

/** Scanners may wrap the value in whitespace; job numbers are plain tokens. */
function normaliseJob(code: string): string {
  return (code || '').trim();
}

function apiErr(e: unknown): string {
  const x = e as { response?: { data?: { error?: string } }; message?: string };
  return x?.response?.data?.error ?? x?.message ?? 'Failed';
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '—';

/**
 * The press-unit badge for one separation on the sticker: "U3", "C1", or null
 * when the colour hasn't been given a unit yet.
 *
 * data.units / data.coatings ARE the saved assignment; separations_in_order is
 * the artwork's own file order. Joining them here keeps the row order the press
 * expects to read while still naming the pot.
 */
/**
 * Print order for the sticker's rows: **U1 → U7, then the coaters, then anything
 * with no unit** (Jack, 14/09 — "Unit 1 → Unit 7 makes most sense").
 *
 * The rows used to come out in the artwork file's own order, which is whatever
 * the designer happened to build the separations in — meaningless to the printer
 * standing at the press, who loads pots left to right. Sorting by the SAVED
 * assignment makes the sticker read in the order the job is actually set up.
 *
 * Colours with no unit yet sort last rather than being dropped: an unfinished
 * assignment must stay visible on the bag, not vanish off the bottom.
 */
function stickerSortKey(data: InkStickerData, sep: { name: string; ink_key: string | null }): number {
  const badge = unitBadge(data, sep);
  if (!badge) return 999;                        // no unit — last
  if (badge.startsWith('U')) return Number(badge.slice(1)) || 99;
  if (badge.startsWith('C')) return 100 + (Number(badge.slice(1)) || 0);  // coaters after the units
  return 998;
}

function unitBadge(data: InkStickerData, sep: { name: string; ink_key: string | null }): string | null {
  const key = sep.name.trim().toLowerCase();
  const unit = data.units.find(
    (u) => u.name.trim().toLowerCase() === key
      || (!!sep.ink_key && u.ink_key === sep.ink_key),
  );
  if (unit) return `U${unit.unit}`;
  const coat = data.coatings.find(
    (c) => c.name.trim().toLowerCase() === key
      || (!!sep.ink_key && c.ink_key === sep.ink_key),
  );
  return coat ? `C${coat.slot}` : null;
}

interface StickerState {
  data?: InkStickerData;
  thumbUrl?: string | null;
  status: 'loading' | 'ready' | 'error';
  error?: string;
}

/**
 * Load a whole run's stickers for batch printing. Each job is pulled through the
 * shared serial() queue — one Cloudflow call at a time, never bursting
 * wave.abaris.com.au (the house rule; the board pre-warms scheduled runs so most
 * are cache hits). A failed thumbnail still yields a printable sticker (no art);
 * only a failed sticker fetch is a hard error. Print gates on `done`.
 */
function useRunStickers(jobs: string[], skipArtwork: boolean) {
  const [results, setResults] = useState<Record<string, StickerState>>({});
  const cancelled = useRef(false);
  const key = jobs.join(',');

  useEffect(() => {
    cancelled.current = false;
    if (jobs.length === 0) { setResults({}); return; }
    setResults(Object.fromEntries(jobs.map((j) => [j, { status: 'loading' } as StickerState])));

    const set = (job: string, patch: Partial<StickerState>) =>
      setResults((r) => ({ ...r, [job]: { ...r[job], ...patch } }));

    jobs.forEach((job) => serial(async () => {
      if (cancelled.current) return;
      try {
        const data = await getInkSticker(job);
        if (cancelled.current) return;
        if (skipArtwork) { set(job, { data, thumbUrl: null, status: 'ready' }); return; }
        try {
          const t = await getArtworkThumbnail(job, THUMB_WIDTH);
          if (!cancelled.current) set(job, { data, thumbUrl: t.data_url ?? null, status: 'ready' });
        } catch {
          if (!cancelled.current) set(job, { data, thumbUrl: null, status: 'ready' });
        }
      } catch (e) {
        if (!cancelled.current) set(job, { status: 'error', error: apiErr(e) });
      }
    }));

    return () => { cancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, skipArtwork]);

  const ready = jobs.filter((j) => {
    const s = results[j];
    return s && (s.status === 'ready' || s.status === 'error');
  }).length;
  return { results, ready, total: jobs.length, done: jobs.length > 0 && ready === jobs.length };
}

export default function InkStickerView() {
  const [jobNumber, setJobNumber] = useState<string>('');
  const [autoPrint, setAutoPrint] = useState<boolean>(false);
  const [runGroup, setRunGroup] = useState<JobGroup | null>(null);
  const [skipArtwork, setSkipArtwork] = useState<boolean>(false);
  const printedRef = useRef<string>('');

  const onScan = useCallback((code: string) => {
    const j = normaliseJob(code);
    if (j) { setJobNumber(j); setRunGroup(null); }
  }, []);

  // Job numbers are short (~6 digits) vs the WMS default of 18 for SSCC.
  useBarcodeScanner({ onScan, minLength: 4 });

  // Single-job (scan) queries — off while a run is selected.
  const singleOn = !!jobNumber && !runGroup;
  const q = useQuery({
    queryKey: productionKeys.inkSticker(jobNumber),
    queryFn: () => getInkSticker(jobNumber),
    enabled: singleOn,
    staleTime: 60 * 1000,
    retry: false,
  });

  const thumb = useQuery({
    queryKey: jobNumber ? productionKeys.artworkThumbnail(jobNumber, THUMB_WIDTH) : ['ink-sticker-thumb-off'],
    queryFn: () => getArtworkThumbnail(jobNumber, THUMB_WIDTH),
    enabled: singleOn,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  const data = q.data;

  // Run mode — job groups (straight off the board) + the batch loader.
  const groupsQ = useQuery({
    queryKey: productionKeys.jobGroups({ limit: 200 }),
    queryFn: () => getJobGroups({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const runJobs = runGroup?.job_numbers ?? [];
  const run = useRunStickers(runJobs, skipArtwork);
  const pickGroup = (g: JobGroup | null) => { setRunGroup(g); setJobNumber(''); printedRef.current = ''; };

  // Has the artwork query FINISHED, one way or the other? Success, error, or a
  // legitimate "no artwork" all count — what matters is that it's no longer in
  // flight, so nothing is still going to change on the page.
  const thumbSettled = !thumb.isFetching && (thumb.isSuccess || thumb.isError);

  // Auto-print once per successfully loaded job (terminal mode).
  //
  // This USED to fire on a fixed 350ms timer, which is where the "Loading…"
  // stickers came from: the sticker asks for a 360px-wide thumbnail, a width
  // nothing else requests, so the first sticker for any job is always a COLD
  // Cloudflow fetch — seconds, not milliseconds. The timer won that race every
  // time and printed the placeholder.
  //
  // Now the print waits for the artwork to settle. The short timeout that remains
  // is only to let the barcode and the (now-loaded) image paint.
  //
  // Also withheld on an ink_spec_mismatch (Prism says ECG, the resolved
  // artwork still has a Pantone separation) — same reasoning as waiting for
  // the artwork: this is exactly the "wrong file resolved" signal the floor
  // needs to SEE before a sticker goes on a job bag, not one that should
  // print past them silently. The manual Print button stays available.
  useEffect(() => {
    if (singleOn && autoPrint && data && q.isSuccess && thumbSettled && !data.ink_spec_mismatch
        && printedRef.current !== data.job_number) {
      printedRef.current = data.job_number;
      const t = setTimeout(() => window.print(), 350);
      return () => clearTimeout(t);
    }
  }, [singleOn, autoPrint, data, q.isSuccess, thumbSettled]);

  const reset = () => {
    setJobNumber('');
    setRunGroup(null);
    printedRef.current = '';
  };

  const mode: 'idle' | 'scan' | 'run' = runGroup ? 'run' : jobNumber ? 'scan' : 'idle';

  return (
    <Box sx={{ p: 3, bgcolor: TOKENS.bg, minHeight: '100%' }}>
      {/* print stylesheet — only the sticker prints, at B5. Removed on unmount. */}
      <style>{PRINT_CSS}</style>

      {/* ---- controls (never printed) ---- */}
      <Box className="no-print">
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <QrCodeScannerRounded sx={{ color: TOKENS.accent }} />
          <Typography sx={{ fontWeight: 800, fontSize: 20, color: TOKENS.ink }}>
            Ink Sticker
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 13, color: TOKENS.ink2, mb: 2 }}>
          Scan a Job Bag to print one sticker, or pick a run to print the whole forme at once.
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { md: 'center' } }}>
          <ManualScanInput onSubmit={onScan} placeholder="Scan or type job number…" />
          <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>or</Typography>
          <Autocomplete<JobGroup>
            size="small"
            sx={{ minWidth: 340, flexGrow: 1 }}
            options={groupsQ.data ?? []}
            loading={groupsQ.isLoading}
            value={runGroup}
            onChange={(_e, v) => pickGroup(v)}
            filterOptions={(opts, state) => {
              const s = state.inputValue.trim().toLowerCase();
              if (!s) return opts;
              return opts.filter((o) => (
                `${o.customer_code} ${o.customer_name} ${jobGroupLabel(o)} ${o.press_code ?? 'backlog'} ${o.job_numbers.join(' ')}`
                  .toLowerCase().includes(s)
              ));
            }}
            getOptionLabel={(o) => (
              `${o.customer_code} · ${jobGroupLabel(o)} — ${o.job_count} job${o.job_count === 1 ? '' : 's'}`
              + ` · due ${fmtDate(o.earliest_due)} · ${o.press_code ?? 'backlog'}`
            )}
            isOptionEqualToValue={(o, v) => o.group_key === v.group_key}
            renderInput={(params) => (
              <TextField {...params} label="Print a run (a forme or trim = a gang run)"
                helperText={groupsQ.isError ? `Couldn’t load runs: ${apiErr(groupsQ.error)}` : 'Straight off the Planning Board, earliest due first.'}
                error={groupsQ.isError} />
            )}
          />
        </Stack>

        {mode === 'scan' && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            {/* Held until the artwork settles — printing mid-fetch is exactly how
                a 'Loading…' box ends up stuck to a Job Bag. */}
            <Button
              variant="contained"
              startIcon={thumbSettled ? <LocalPrintshopRounded /> : <CircularProgress size={16} color="inherit" />}
              onClick={() => window.print()} disabled={!data || !thumbSettled}
              sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}
            >
              Print
            </Button>
            <Button variant="text" startIcon={<RefreshRounded />} onClick={reset} sx={{ color: TOKENS.ink2 }}>
              Scan another
            </Button>
            <FormControlLabel
              control={<Switch size="small" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>Auto-print on scan</Typography>}
            />
          </Stack>
        )}

        {mode === 'run' && runGroup && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={run.done ? <LocalPrintshopRounded /> : <CircularProgress size={16} color="inherit" />}
                onClick={() => window.print()} disabled={!run.done || run.total === 0}
                sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}
              >
                {run.done ? `Print run (${run.total})` : `Preparing ${run.ready}/${run.total}…`}
              </Button>
              <Button variant="text" startIcon={<RefreshRounded />} onClick={reset} sx={{ color: TOKENS.ink2 }}>
                Clear
              </Button>
              <FormControlLabel
                control={<Switch size="small" checked={skipArtwork} onChange={(e) => setSkipArtwork(e.target.checked)} />}
                label={<Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>Skip artwork (faster)</Typography>}
              />
              <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
                {runGroup.customer_code} · {jobGroupLabel(runGroup)} · {runGroup.press_code ?? 'backlog'}
              </Typography>
            </Stack>
            {!run.done && (
              <LinearProgress variant="determinate" value={run.total ? (run.ready / run.total) * 100 : 0}
                sx={{ mt: 1, borderRadius: 1, maxWidth: 420 }} />
            )}
            {!runGroup.fully_scheduled && (
              <Alert severity="warning" sx={{ mt: 1.25, maxWidth: 620, fontSize: 12.5 }}>
                Only {runGroup.scheduled_count} of {runGroup.job_count} jobs on this forme are scheduled — the rest
                will still print, but the planner hasn’t committed the whole group yet.
              </Alert>
            )}
          </Box>
        )}
      </Box>

      {/* ---- idle ---- */}
      {mode === 'idle' && (
        <Box className="no-print" sx={{
          mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center',
          color: TOKENS.ink3,
        }}>
          <QrCodeScannerRounded sx={{ fontSize: 64, mb: 1, opacity: 0.5 }} />
          <Typography sx={{ fontSize: 15 }}>Scan a Job Bag, or pick a run to print all its stickers.</Typography>
        </Box>
      )}

      {/* ---- single scan ---- */}
      {mode === 'scan' && q.isLoading && (
        <Stack className="no-print" alignItems="center" sx={{ py: 6 }} spacing={1.5}>
          <CircularProgress size={26} />
          <Typography sx={{ fontSize: 13, color: TOKENS.ink3 }}>
            Building sticker for job {jobNumber}…
          </Typography>
        </Stack>
      )}
      {mode === 'scan' && q.isError && (
        <Alert className="no-print" severity="error" sx={{ maxWidth: 480 }}>
          Couldn’t load job {jobNumber}: {(q.error as Error)?.message ?? 'unknown error'}
        </Alert>
      )}
      {mode === 'scan' && data && data.ink_spec_mismatch && (
        <Alert className="no-print" severity="error" sx={{ maxWidth: 620, mb: 1.5 }}>
          <strong>Prism specifies ECG, but this artwork still has a Pantone/PMS separation.</strong>{' '}
          The resolved file is likely the wrong version — check the artwork path below before
          printing. Auto-print is on hold for this job; Print still works manually if you've
          confirmed it's correct.
        </Alert>
      )}
      {mode === 'scan' && data && (
        <Box id="ink-print-root">
          <Sticker
            data={data}
            thumbUrl={thumb.data?.data_url ?? null}
            thumbLoading={!thumbSettled}
            thumbError={thumb.isError ? ((thumb.error as Error)?.message ?? 'Artwork preview failed') : null}
          />
        </Box>
      )}
      {mode === 'scan' && data && (
        <ArtworkPathControl data={data} jobNumber={jobNumber} />
      )}

      {/* ---- run: one sticker per job, one per printed page ---- */}
      {mode === 'run' && (
        <>
          {/* jobs still loading / errored — status only, never printed */}
          {runJobs.some((j) => run.results[j]?.status !== 'ready') && (
            <Stack className="no-print" direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.5 }}>
              {runJobs.map((j) => {
                const s = run.results[j];
                if (s?.status === 'ready') return null;
                return (
                  <Chip key={j} size="small"
                    icon={s?.status === 'error' ? undefined : <CircularProgress size={11} />}
                    label={s?.status === 'error' ? `${j} — ${s.error}` : j}
                    sx={{
                      fontFamily: TOKENS.monoFont, fontWeight: 700,
                      color: s?.status === 'error' ? TOKENS.bad : TOKENS.ink3,
                      bgcolor: s?.status === 'error' ? 'rgba(220,38,38,0.08)' : TOKENS.lineSoft,
                    }} />
                );
              })}
            </Stack>
          )}
          {/* the printable stickers — only ready ones, so page breaks stay clean */}
          <Box id="ink-print-root" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {runJobs.map((j) => {
              const s = run.results[j];
              return s?.status === 'ready' && s.data
                ? <Sticker key={j} data={s.data} thumbUrl={s.thumbUrl ?? null} thumbLoading={false} thumbError={null} />
                : null;
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------

function Sticker({ data, thumbUrl, thumbLoading, thumbError }: {
  data: InkStickerData; thumbUrl: string | null;
  thumbLoading: boolean; thumbError: string | null;
}) {
  const artworkMissing = data.artwork_status === 'not_found' || data.artwork_status === 'error'
    || data.artwork_status === 'no_path';

  return (
    <Box className="ink-sticker-print" sx={{
      mt: 1, width: SCREEN_WIDTH_PX, height: SCREEN_HEIGHT_PX, maxWidth: '100%',
      boxSizing: 'border-box', overflow: 'hidden', bgcolor: '#fff',
      // solid black outline = the guillotine cut line once printed — square
      // corners, since a straight-blade cut can't reproduce a radius
      border: '2px solid #000', borderRadius: 0, p: 1.25,
      boxShadow: TOKENS.shadowSm,
      // ensure the colour swatches actually print
      WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
      color: TOKENS.ink,
      // Flex column so the artwork block (flexGrow below) can absorb
      // whatever room a job's separation count leaves unused, while the
      // Prism ink / path footer stays pinned to the fixed box's bottom
      // edge regardless of how much — or little — is above it.
      display: 'flex', flexDirection: 'column',
    }}>
      {/* header: job number + barcode */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ flexShrink: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, letterSpacing: 0.5, color: TOKENS.ink3, textTransform: 'uppercase' }}>
            Job
          </Typography>
          <Typography sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 26, lineHeight: 1 }}>
            {data.job_number}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Barcode value={data.job_number} />
        </Box>
      </Stack>

      {/* identity line */}
      <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.75, lineHeight: 1.2, flexShrink: 0 }}>
        {data.description || '—'}
      </Typography>
      <Typography sx={{ fontSize: 12, color: TOKENS.ink2, mt: 0.25, flexShrink: 0 }}>
        <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>
          {data.customer_ref || '—'}
        </Box>
        {'  ·  '}{data.customer_name || data.customer_code || '—'}
        {'  ·  Forme '}
        <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>
          {data.forme_number || '—'}
        </Box>
      </Typography>

      {/* material / run meta */}
      <Box sx={{
        mt: 0.75, mb: 0.75, px: 1, py: 0.5, borderRadius: '7px', bgcolor: TOKENS.lineSoft,
        display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', flexShrink: 0,
      }}>
        <Meta label="Material" value={data.material_desc || '—'} />
        <Meta label="Sheet" value={
          data.sheet_width_mm && data.sheet_height_mm
            ? `${data.sheet_width_mm} × ${data.sheet_height_mm} mm` : '—'}
          sub={data.sheet_area_m2 != null ? `${data.sheet_area_m2.toFixed(2)} m²` : undefined} />
        <Meta label="Run" value={
          data.run_length_sheets != null
            ? `${data.run_length_sheets.toLocaleString()} sh` : '—'} />
      </Box>

      {/* Separations in the artwork's own file order, each tagged with the PRESS
          UNIT it runs on (Jack, 09/09: the operator has to see which colour goes
          in which pot at a glance). The tag is joined from data.units, which is
          the saved assignment — a colour with no unit shows a hollow "—" badge,
          which is itself the signal that the assignment is unfinished.
          flexShrink: 0 is load-bearing: this box's own overflow:'hidden'
          (for the border-radius corner clipping) resets the flex default
          "never shrink below content" protection to 0 — without this, the
          ARTWORK preview isn't the only shrinkable thing anymore, and every
          colour row becomes fair game to get clipped under the fixed box's
          overflow guard instead of the preview image giving way first. */}
      <Box sx={{ border: `1px solid ${TOKENS.lineSoft}`, borderRadius: '7px', overflow: 'hidden', flexShrink: 0 }}>
        {data.separations_in_order.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: TOKENS.ink3, px: 1, py: 0.75 }}>
            No separations read from artwork.
          </Typography>
        ) : [...data.separations_in_order]
          .map((sep, idx) => ({ sep, idx, key: stickerSortKey(data, sep) }))
          // Stable within a unit: fall back to the artwork's own order, so two
          // colours that somehow share a unit keep a deterministic sequence.
          .sort((a, b) => (a.key - b.key) || (a.idx - b.idx))
          .map(({ sep: s }, i) => {
          // Which pot this colour goes in. Matched on the artwork separation name
          // first (that's what data.units carries too) and on the allocated ink
          // key as a fallback, so a renamed separation still finds its unit.
          const slot = unitBadge(data, s);
          // Series + type + the coverage/area/run/mileage working, all on ONE
          // line (was three) — the row count is fixed at up to 9 (7 ink units +
          // 2 coatings), so per-row height is what decides whether a full sheet
          // fits the fixed box. noWrap + ellipsis: this line truncates from the
          // tail (the audit-trail maths) rather than ever growing the row.
          const working = demandWorkingText(s.coverage_frac, data.sheet_area_m2, data.run_length_sheets, s.mileage_g_per_m2);
          return (
          <Stack key={`${s.name}-${i}`} direction="row" alignItems="center" spacing={0.85}
            sx={{
              px: 1, py: 0.45,
              borderTop: i === 0 ? 'none' : `1px solid ${TOKENS.lineSoft}`,
              // series colour tints the whole row (physical segmentation cue)
              bgcolor: s.series_hex ? `${s.series_hex}40` : 'transparent',
            }}>
            {/* The pot number, hard up against the swatch: colour → unit is the
                one pairing the operator reads off this sticker. */}
            <Box sx={{
              width: 26, height: 20, borderRadius: '4px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: TOKENS.monoFont, fontSize: 11.5, fontWeight: 800,
              color: slot ? TOKENS.ink : TOKENS.ink3,
              bgcolor: slot ? 'rgba(255,255,255,0.75)' : 'transparent',
              border: `1.5px ${slot ? 'solid' : 'dashed'} rgba(0,0,0,0.35)`,
            }}>
              {slot ?? '—'}
            </Box>
            <Box sx={{
              width: 14, height: 14, borderRadius: '3px', flexShrink: 0,
              bgcolor: s.hex_colour ?? '#ccc', border: '1px solid rgba(0,0,0,0.2)',
            }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography noWrap sx={{
                fontSize: 14, fontWeight: 700, lineHeight: 1.1,
                color: s.low_migration ? LMQ_RED : TOKENS.ink,
              }}>
                {/* Show the ALLOCATED ink (its colour), not the artwork separation
                    name — that's what the operator loads. Fall back to the
                    separation only when no ink is allocated (nothing else to show). */}
                {s.ink_colour || s.name}
                {s.low_migration && (
                  <Box component="span" sx={{ ml: 0.5, fontSize: 9.5, fontWeight: 800, color: LMQ_RED }}>LMQ</Box>
                )}
              </Typography>
              {(s.series || working) && (
                <Typography noWrap title={s.kind ? s.kind.replace('_', ' ') : undefined}
                  sx={{ fontSize: 10.5, lineHeight: 1.2, color: s.low_migration ? LMQ_RED : TOKENS.ink2 }}>
                  {s.series}
                  {/* The PROCESS / SPOT tag was dropped (Jack, 11/09): on a
                      noWrap line it was eating the width the demand working
                      needs, and the working itself already shows the rate that
                      the classification would have explained (1.2 vs 1.5). The
                      kind is still on the row's `title` for anyone who wants it. */}
                  {s.series && working && '  '}
                  {working && (
                    <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontSize: 9, color: TOKENS.ink3 }}>
                      {working}
                    </Box>
                  )}
                </Typography>
              )}
            </Box>
            <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
              <Typography sx={{
                fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                color: TOKENS.ink2, lineHeight: 1.1,
              }}>
                {kg(s.demand_kg)}
              </Typography>
            </Box>
          </Stack>
          );
        })}
      </Box>

      {/* Artwork preview — left-aligned, and the ONE flex-grow region on the
          sticker: it absorbs whatever height a job's separation count leaves
          unused, so a short ink table doesn't just leave blank space while a
          long one crowds everything else. minHeight: 0 is load-bearing here —
          without it a flex child won't shrink below its content size, which
          defeats the fixed-box overflow guard entirely. */}
      <Box sx={{ mt: 1, flexGrow: 1, minHeight: 0, display: 'flex' }}>
        <Box sx={{
          maxWidth: '55%', maxHeight: '100%', borderRadius: '6px', overflow: 'hidden',
          border: `1px solid ${TOKENS.lineSoft}`, bgcolor: TOKENS.lineSoft,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {/* Four distinct states. 'Loading…' used to be the catch-all, so a FAILED
              preview looked identical to one still in flight — and, worse, got
              printed onto the sticker. Auto-print now waits for this to settle, so
              a spinner here should never reach paper. */}
          {thumbUrl ? (
            <Box component="img" src={thumbUrl} alt={`Artwork ${data.job_number}`}
              sx={{ maxWidth: '100%', maxHeight: '100%', display: 'block', objectFit: 'contain' }} />
          ) : thumbLoading ? (
            <Stack alignItems="center" spacing={0.5}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 9, color: TOKENS.ink3 }}>Reading artwork…</Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 9.5, color: TOKENS.ink3, textAlign: 'center', px: 1, lineHeight: 1.3 }}>
              {artworkMissing || !thumbError ? 'No artwork preview' : 'Artwork preview unavailable'}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Footer — Prism ink cross-check, caveats, artwork path. flexShrink: 0
          (never compressed) and never grows either, so this sits pinned to
          the fixed box's bottom edge regardless of how much room the
          artwork block above claimed. */}
      <Box sx={{ flexShrink: 0 }}>
        {/* Prism's own ink build string — a cross-check against the computed
            list above, not derived from it, so it's worth keeping even when
            the two agree. Shown as-is; no attempt to parse or reconcile it. */}
        {data.print_spec && (
          <Box sx={{ mt: 0.75 }}>
            <Typography sx={{ fontSize: 9, letterSpacing: 0.3, color: TOKENS.ink3, textTransform: 'uppercase' }}>
              Prism ink
            </Typography>
            <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, fontWeight: 600, color: TOKENS.ink2, lineHeight: 1.3, wordBreak: 'break-word' }}>
              {data.print_spec}
            </Typography>
          </Box>
        )}

        {(artworkMissing || data.warnings.length > 0) && (
          <>
            <Divider sx={{ my: 0.75 }} />
            {/* The ECG/Pantone mismatch is pulled out of the generic warnings
                list and rendered in red/bold on its own — this is the one
                that needs to stop someone reaching for the wrong ink, not
                just a routine caveat, so it can't read like one. */}
            {data.ink_spec_mismatch_detail && (
              <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#dc2626', lineHeight: 1.3, mb: 0.5 }}>
                ⚠ {data.ink_spec_mismatch_detail}
              </Typography>
            )}
            {(artworkMissing || data.warnings.some((w) => w !== data.ink_spec_mismatch_detail)) && (
              <Typography sx={{ fontSize: 9.5, color: TOKENS.ink3, lineHeight: 1.3 }}>
                {artworkMissing
                  ? 'Artwork not found — units/series shown from saved assignment; kg unavailable.'
                  : data.warnings.filter((w) => w !== data.ink_spec_mismatch_detail).join(' ')}
              </Typography>
            )}
          </>
        )}

        {/* The exact file this sticker was built from — printed so a floor
            discrepancy (wrong art, stale reprint) can be traced back to a
            specific path without going near a screen. */}
        {data.artwork_path && (
          <Typography sx={{
            mt: 0.5, fontFamily: TOKENS.monoFont, fontSize: 7.5, color: TOKENS.ink3,
            lineHeight: 1.25, wordBreak: 'break-all',
          }}>
            {/* Flags a manually-corrected path so a floor discrepancy (or a
                future re-file that quietly breaks it) isn't read as "the
                system found this the normal way" — bold + a symbol, not just
                colour, since this prints and may not print in colour. */}
            {data.artwork_path_overridden && (
              <Box component="span" sx={{ fontWeight: 800, letterSpacing: 0.3, color: TOKENS.accent, mr: 0.5 }}>
                ✎ MODIFIED —
              </Box>
            )}
            {data.artwork_path}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Artwork path — reviewable and overridable REGARDLESS of artwork_status.
 * A path can resolve to the wrong file just as easily as to no file at all,
 * so this isn't gated behind "not found". Saved against customer_code +
 * customer_ref (the SKU, not the job), so fixing it once covers every job
 * on that finished-good from here on — no re-finding the same artwork twice.
 */
function ArtworkPathControl({ data, jobNumber }: { data: InkStickerData; jobNumber: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // Shared with Ink Assign: same override table, same re-pull, so a path fixed
  // from either screen is fixed for both — and both get the Cloudflow browser
  // rather than one of them keeping a paste-only box.
  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: productionKeys.inkSticker(jobNumber) });
    queryClient.invalidateQueries({ queryKey: ['production', 'ink-assignment'] });
    queryClient.invalidateQueries({ queryKey: productionKeys.inkDemandSummary() });
  };

  const statusOk = data.artwork_status === 'resolved';
  const canEdit = !!data.customer_code && !!data.customer_ref;

  return (
    <Box className="no-print" sx={{
      mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, maxWidth: 620,
    }}>
      <Chip size="small" label={statusOk ? 'Artwork resolved' : 'Artwork not found'}
        sx={{
          fontSize: 11, fontWeight: 700,
          color: statusOk ? TOKENS.good : TOKENS.warn,
          bgcolor: statusOk ? 'rgba(22,163,74,0.08)' : 'rgba(245,158,11,0.08)',
        }} />
      {data.artwork_path_overridden && (
        <Chip size="small" label="✎ Modified" sx={{ fontSize: 11, fontWeight: 700, color: TOKENS.accent, bgcolor: `${TOKENS.accent}18` }} />
      )}
      <Typography noWrap sx={{
        fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink3, flexGrow: 1, minWidth: 0,
      }}>
        {data.artwork_path || '—'}
      </Typography>
      <IconButton size="small" disabled={!canEdit} onClick={() => setOpen(true)}>
        <EditRounded fontSize="small" />
      </IconButton>

      {open && canEdit && (
        <ArtworkPathDialog
          open
          customerCode={data.customer_code || ''}
          customerRef={data.customer_ref || ''}
          currentPath={data.artwork_path}
          overridden={data.artwork_path_overridden}
          onClose={() => setOpen(false)}
          onSaved={onSaved}
        />
      )}
    </Box>
  );
}

function Meta({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 9, letterSpacing: 0.3, color: TOKENS.ink3, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 600, color: TOKENS.ink }}>
        {value}
      </Typography>
      {sub && (
        <Typography noWrap sx={{ fontSize: 10, fontWeight: 700, color: TOKENS.ink2, lineHeight: 1.15 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The demand maths for one line, spelled out for an auditable trail:
 *   coverage × sheet_area_m² × run_sheets × mileage(g/m²) ÷ 1000 = kg
 * Plain string (not its own Typography block) — composed inline into the
 * row's single detail line so per-row height stays fixed regardless of
 * whether this renders. Null when any factor is missing (reel / uncosted).
 */
function demandWorkingText(
  coverage: number | null, areaM2: number | null, run: number | null, mileage: number | null,
): string | null {
  if (coverage == null || areaM2 == null || run == null || mileage == null) return null;
  const covPct = coverage * 100;
  const cov = covPct >= 100 ? '100' : covPct < 1 ? covPct.toFixed(2) : covPct.toFixed(1);
  return `${cov}% × ${areaM2.toFixed(2)}m² × ${run.toLocaleString()}sh × ${mileage}g/m²`;
}

/** Code 128 of the job number — enables the Phase-4 depletion scan later. */
function Barcode({ value }: { value: string }) {
  const ref = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    if (ref.current && value) {
      try {
        JsBarcode(ref.current, value, {
          format: 'CODE128',
          displayValue: false,
          height: 38,
          width: 1.6,
          margin: 0,
          background: 'transparent',
        });
      } catch {
        /* invalid value — leave the svg empty */
      }
    }
  }, [value]);
  return <svg ref={ref} style={{ maxWidth: 150 }} />;
}

// Only the sticker(s) print, one per B5 page. The body-visibility trick keeps it
// robust against the surrounding app chrome without unmounting anything; the
// print root is pinned to the page origin and each sticker breaks to its own page,
// so a single scan and a whole run share the same CSS.
const PRINT_CSS = `
@media print {
  @page { size: A4 portrait; margin: 3mm; }
  /* A stray UA margin on html/body shifts the whole page content down,
     which is what a "why is this centred" report usually turns out to be. */
  html, body { margin: 0 !important; padding: 0 !important; }
  body * { visibility: hidden !important; }
  #ink-print-root, #ink-print-root * { visibility: visible !important; }
  #ink-print-root {
    position: absolute !important;
    left: ${PRINTER_CALIBRATION_X_MM}mm; top: ${PRINTER_CALIBRATION_Y_MM}mm;
    width: 100%;
  }
  .ink-sticker-print {
    width: ${STICKER_WIDTH_MM}mm !important;
    height: ${STICKER_HEIGHT_MM}mm !important;
    max-width: ${STICKER_WIDTH_MM}mm !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    margin: 0 !important;
    border: 2px solid #000 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    break-inside: avoid;
    break-after: page;
  }
  .ink-sticker-print:last-of-type { break-after: auto; }
  .no-print { display: none !important; }
}
`;
